/**
 * Client-side gating for the Data Validation (recon errors) feature.
 *
 * Resolved from the org config already in the redux store, so a tenant without
 * the flag issues no extra request: the banner and the close tile must decide
 * whether to fetch *before* fetching.
 */

export const RECON_ERRORS_CONFIG_ID = "properties/recon-errors-enabled";

/**
 * Strict allowlist rather than a truthiness test: booleans are stored as the
 * string "false" for some legacy tenants (see Feature.js), and the visibility
 * default has to be "hidden" for every tenant that has no row at all —
 * `defaultOrgConfig` only applies at tenant creation and never backfills.
 *
 * @param {Array<{id: string, value: unknown}>} orgConfig org configurations from redux
 * @returns {boolean}
 */
export function isReconErrorsEnabled(orgConfig) {
    if (!Array.isArray(orgConfig)) {
        return false;
    }

    const config = orgConfig.find(
        (cfg) => cfg && cfg.id === RECON_ERRORS_CONFIG_ID
    );
    const value = config && config.value;

    return value === true || value === "true";
}

export function reconErrorsPath(orgId) {
    return `/${orgId}/recon-errors`;
}

/** Filter sentinels, matching `ReconErrorsManager`'s `ALL` / `NO_PERIOD`. */
export const ALL = "All";
export const NO_PERIOD = "No period";

/**
 * Matches `ReconErrorsManager.BLOCKS_CLOSE`. Narrows to what the close guard
 * would actually refuse, which is the number the headline card reports.
 */
export const BLOCKS_CLOSE = "Blocks this close";

/**
 * The two choices the scope select offers, in place of the list of months it
 * used to build from the data.
 *
 * A stored `Actg_Period` is "the earliest close this can still be fixed in":
 * it is floored at the tenant's open period, so no check can ever stamp a
 * closed month, and every sync restamps it as the tenant closes months. The
 * dropdown therefore never held more than two real months for any tenant, and
 * reading it as history gave a closed month an all-clear nobody had checked.
 *
 * `Blocks this close` is deliberately not paired with a "not period-specific"
 * option: a periodless row blocks whichever close is attempted, so it sits
 * inside this filter rather than opposite it. Periodless rows are still named
 * as such where it matters — on the row itself, via `NO_PERIOD_LABEL`.
 */
export const PERIOD_SCOPES = [ALL, BLOCKS_CLOSE];

/**
 * The page opens close-scoped, so the first thing a controller reads is what
 * stands between them and this month's close — errors and ignores alike.
 *
 * `All` stays one click away but is opt-in, for two reasons. A tenant carrying
 * three years of findings is not helped by all of it at once, and the
 * unscoped read is the expensive one: `Actg_Period` is part of the JobErrors
 * hash key, so the scoped query stays indexed while `All` aggregates
 * everything the tenant has ever recorded.
 */
export const DEFAULT_PERIOD_SCOPE = BLOCKS_CLOSE;

/**
 * One line under the filters saying which universe of errors is on screen.
 *
 * Worth the space because the two scopes can differ by orders of magnitude,
 * and because a scoped view reports a check with no rows in range as passing
 * — true of this close, and easy to misread as true of the tenant.
 */
export function scopeCaption(period, openPeriod) {
    if (period !== BLOCKS_CLOSE) {
        return (
            "Showing errors from every period, including ignored ones " +
            "already resolved."
        );
    }

    return openPeriod
        ? `Showing errors that block the close of ${openPeriod}.`
        : "Showing errors that block the next close.";
}

/**
 * Eight of the eleven checks emit a NULL `Actg_Period` on purpose: the
 * completeness checks ask whether a record reached the warehouse at all, and the
 * control checks compare lifetime-to-date balances. Neither can be sliced by
 * period, so periodless rows are the common case rather than missing data, and
 * "No period" reads as the latter.
 *
 * Wording only — `NO_PERIOD` remains the value sent to the server, which matches
 * it to `Actg_Period IS NULL`.
 */
export const NO_PERIOD_LABEL = "Not period-specific";

export const NO_PERIOD_HINT =
    "This check is not period-specific: it compares lifetime balances, or " +
    "whether the record reached the warehouse at all.";

export const PERIOD_NONE = "NONE";
export const PERIOD_HEADER = "HEADER";
export const PERIOD_COLUMN = "COLUMN";

/**
 * Where a check's period belongs: nowhere, in the panel header, or in its own
 * column.
 *
 * Period scope is a property of the check rather than of the row, so a column
 * only earns its place when the rows disagree. Eight of the eleven checks name
 * no period at all and would render a column of dashes; the rest often report
 * a single period, where the column repeats one value down every row. Both say
 * more in the header, which is what came out of the review.
 *
 * Defaults to the column. The API and the app deploy separately, and an API
 * that predates these fields sends neither — dropping the column on an absent
 * field would hide a real period from the journal and rollforward checks for
 * the length of the rollout.
 */
export function periodDisplay(check) {
    if (check && check.hasPeriods === false) {
        return { mode: PERIOD_NONE, period: null };
    }

    if (check && check.commonPeriod) {
        return { mode: PERIOD_HEADER, period: check.commonPeriod };
    }

    return { mode: PERIOD_COLUMN, period: null };
}

export function periodFilterLabel(period) {
    return period === NO_PERIOD ? NO_PERIOD_LABEL : period;
}

export const STATUS_ACTIVE = "Active";
export const STATUS_IGNORED = "Ignored";

/**
 * Outcome of one check, matching `CHECK_STATUS` in
 * `packages/core/src/recon/reconCheckStatus.ts`.
 *
 * Uncleared errors mean failed and their absence means passed. `NOT_APPLICABLE`
 * is not a third shade of that: it means the check is gated off for this tenant
 * and will never produce a result, so green would be a claim nothing supports.
 */
export const CHECK_PASSED = "PASSED";
export const CHECK_FAILED = "FAILED";
export const CHECK_NOT_APPLICABLE = "NOT_APPLICABLE";

const CHECK_STATUS_LABELS = {
    [CHECK_PASSED]: "Passed",
    [CHECK_FAILED]: "Failed",
    [CHECK_NOT_APPLICABLE]: "Not applicable"
};

export function checkStatusLabel(status) {
    return CHECK_STATUS_LABELS[status] || status;
}

/**
 * Colours are stated here rather than in the theme because they carry meaning
 * the palette does not: a controller reads this column before reading anything
 * else on the page.
 */
export const CHECK_STATUS_COLORS = {
    [CHECK_PASSED]: { backgroundColor: "#e6f4ea", color: "#1e7e34" },
    [CHECK_FAILED]: { backgroundColor: "#fdecea", color: "#d32f2f" },
    [CHECK_NOT_APPLICABLE]: { backgroundColor: "#eeeeee", color: "#616161" }
};

/** The shape the page and the dashboard section both assume. */
export const EMPTY_RECON_SUMMARY = {
    groups: [],
    checks: [],
    filtered: false,
    totals: {
        blocking: 0,
        warnings: 0,
        ignored: 0,
        codes: 0,
        checks: 0,
        passed: 0
    },
    filterOptions: { entities: [] },
    lastJob: { jobId: null, runOn: null },
    lastValidatedOn: null,
    hasUnresolvedOutsideScope: false
};

/**
 * Fills in anything the response is missing.
 *
 * The API and the app deploy separately, so for the length of a rollout this
 * client can be talking to a version that predates `checks` / `totals.checks`.
 * Defaulting here keeps that window rendering an honest empty state instead of
 * "3 of undefined" or a crash inside a `.map`.
 */
export function normalizeReconSummary(result) {
    if (!result) return EMPTY_RECON_SUMMARY;

    return {
        ...EMPTY_RECON_SUMMARY,
        ...result,
        totals: { ...EMPTY_RECON_SUMMARY.totals, ...(result.totals || {}) },
        filterOptions: {
            ...EMPTY_RECON_SUMMARY.filterOptions,
            ...(result.filterOptions || {})
        },
        lastJob: { ...EMPTY_RECON_SUMMARY.lastJob, ...(result.lastJob || {}) }
    };
}

/** Tone of the dashboard tile, which decides its icon and colour. */
export const CARD_HEALTHY = "healthy";
export const CARD_FAILING = "failing";
export const CARD_NOT_RUN = "notRun";

/**
 * The dashboard tile's three lines, derived from the same summary the Data
 * Validation page reads so the two can never disagree.
 *
 * Counts CHECKS rather than records: "2 of 11" is the number a controller can
 * act on, where "14" is a record count whose size depends on how far one bad
 * order fans out. The record count stays in the caption, behind the reason it
 * matters — a blocked close.
 *
 * Failures are reported even when the last run cannot be resolved: an
 * uncleared error outlives the job that found it. The reverse is not true, so
 * a tenant that has never synced reads as "not validated" rather than green.
 *
 * "Has anything run" is read off the check outcomes rather than off
 * `lastJob`, because the server can establish that checks passed without
 * resolving the job that ran them — job records expire out of redis while the
 * errors they produced live on in the warehouse. Keying on `lastJob` would put
 * "Not validated yet" on the tile next to a page listing passing checks.
 */
export function reconCardSummary(summary) {
    const totals = (summary && summary.totals) || {};
    const checks = totals.checks || 0;
    const failing = totals.codes || 0;
    const passed = totals.passed || 0;
    const records = (totals.blocking || 0) + (totals.warnings || 0);

    // "Close blocked" is the close guard's verdict, not a property of the
    // records on the page, so it is keyed on the server's close-scoped count
    // and on nothing else. An absent count is unknown rather than zero, and
    // unknown is not a block: falling back to the in-view total made the tile
    // promise a refusal for a tenant whose close the guard would allow.
    const blockingNextClose =
        typeof totals.blockingNextClose === "number"
            ? totals.blockingNextClose
            : null;

    if (failing) {
        return {
            tone: CARD_FAILING,
            value: `${failing} of ${checks}`,
            caption: blockingNextClose
                ? `Close blocked · ${blockingNextClose.toLocaleString()} records`
                : `${records.toLocaleString()} records to review`
        };
    }

    if (!passed) {
        return {
            tone: CARD_NOT_RUN,
            value: "--",
            caption: "Not validated yet"
        };
    }

    return {
        tone: CARD_HEALTHY,
        value: `${passed} of ${checks}`,
        // Scoped to the close like the page, so "passed" can be true of this
        // close while older unresolved rows sit outside it. Saying which one
        // it is beats a green tile over a backlog.
        caption:
            summary && summary.hasUnresolvedOutsideScope
                ? "passed for this close"
                : "checks passed"
    };
}

const CLOSE_BLOCKED = "Period close is blocked";
const CLOSE_NOT_BLOCKED = "Period close is not blocked";
const CLOSE_UNKNOWN = "Unable to check";

/**
 * The Data Validation page's close card: its number and the sentence under it.
 *
 * Both come from here so they cannot answer different questions. The card used
 * to show `blocking` — blocking records in the current view, across every
 * period — above a verdict derived from `blockingNextClose`, which is scoped to
 * the range the next close covers and stands down when a tenant is not
 * enforcing yet. That let the card read "14" over "Period close is not
 * blocked": both true, and together unreadable. Now the count IS the verdict's
 * subject, and the in-view record total stays where it belongs, on the check
 * list below.
 *
 * @param {{blockingNextClose?: number|null}} totals `getSummary` totals
 * @param {boolean} [failed] whether the summary request itself errored
 * @returns {{value: number|string, hint: string}}
 */
export function closeBlockingCard(totals, failed) {
    const count = totals && totals.blockingNextClose;

    // A failed request and a server that could not resolve the close range say
    // the same thing to a controller: nobody knows. Showing a zero here would
    // report a clear close on the strength of no information at all.
    if (failed || typeof count !== "number") {
        return { value: "--", hint: CLOSE_UNKNOWN };
    }

    return {
        value: count,
        hint: count ? CLOSE_BLOCKED : CLOSE_NOT_BLOCKED
    };
}

/**
 * Count to show for a check under the selected status filter, or null when the
 * check has no records to count. Passing and not-applicable checks render a
 * dash instead of a zero: a zero next to "Passed" invites the reader to look
 * for the records behind it.
 *
 * The count decides whether the row opens, so this has to ask what the selected
 * status actually selects. Keying off `ignoredTotal` instead missed the rows a
 * later sync auto-cleared -- a check holding only those is PASSED with zero
 * ignored, so it was listed under All, for those very rows, and then dashed and
 * sealed shut. A failing check keeps a real zero, where a dash would read as
 * "not measured" rather than "none under this filter".
 */
export function checkCountForStatus(check, status) {
    const total = groupTotalForStatus(check, status);

    if (!total && check.status !== CHECK_FAILED) return null;

    return total;
}

/**
 * Every check inner-joins the orders the sync it ran for actually touched, so
 * an ignored error is only ever re-raised for a record that syncs again. The
 * earlier wording promised a return "after the next sync", which is a promise
 * we cannot keep for a record that goes quiet: that ignore holds indefinitely,
 * and saying otherwise invites a controller to treat it as a reminder.
 */
export const IGNORE_REAPPEAR_WARNING =
    "An ignored error returns and blocks period close again only if the " +
    "record is re-synced with the mismatch unfixed. A record that is never " +
    "re-synced stays ignored.";

/**
 * `Details.Missing_In` holds the warehouse table the record never reached, and
 * can list several separated by commas. Those names mean nothing to a customer,
 * so the page shows the business area instead.
 */
const MISSING_IN_LABELS = {
    SalesOrders_: "Sales orders",
    SalesOrderDocument: "Sales orders",
    RevenueArrangementDocument: "Revenue arrangements",
    BillingSchedule_: "Billing schedule",
    BillingScheduleDocument: "Billing schedule",
    Customers: "Customers",
    CustomersDocument2: "Customers",
    PSD_: "Service delivery",
    ProfessionalServiceDeliveryDocument: "Service delivery",
    Unearned_: "Deferred revenue"
};

/**
 * @param {string} missingIn raw `Missing_In` value, possibly comma-separated
 * @returns {string} de-duplicated business labels, or the raw value when a table
 *   has no mapping yet — showing the table name beats showing nothing.
 */
export function missingInLabel(missingIn) {
    if (!missingIn) return "";

    const labels = String(missingIn)
        .split(",")
        .map((table) => table.trim())
        .filter((table) => table.length)
        .map((table) => MISSING_IN_LABELS[table] || table);

    return Array.from(new Set(labels)).join(", ");
}

/**
 * Count to show for a check under the selected status filter. The server returns
 * all three totals unfiltered so the severity cards stay still when the user
 * flips the status, which leaves only the selection here.
 *
 * `ignoredTotal` is read rather than derived as `total - activeTotal`: a sync
 * auto-clears resolved rows, and those are not ignores. Subtracting would show a
 * count on a check whose Ignored grid is empty.
 */
/**
 * Whether the status filter selects a subset of the records rather than all of
 * them, and so cannot support a verdict on checks it excludes.
 *
 * Only Ignored does. `All` is the widest status — it selects more records than
 * the default — so treating it as a filter had widening the record set remove
 * checks from the list, and hid the checks that had passed outright.
 */
export function narrowsToRecordSubset(status) {
    return status === STATUS_IGNORED;
}

export function groupTotalForStatus(group, status) {
    if (status === STATUS_ACTIVE) return group.activeTotal;
    if (status === STATUS_IGNORED) return group.ignoredTotal;

    return group.total;
}

/**
 * Whether the check row offers a way into its records under the selected
 * status. Active is uncleared rows only, so a passed check holding ignored
 * records stays shut until the user picks Ignored or All.
 */
export function isCheckExpandable(check, status) {
    return groupTotalForStatus(check, status) > 0;
}

/**
 * What the count column is counting, which is whatever `groupTotalForStatus`
 * just selected. The header was fixed at "Failed", so filtering to Ignored put
 * a check's ignored rows under the word "Failed" — the reader then has a count
 * of 3 labelled as failures on a check that has 9 of them.
 *
 * "Records" under All because that total mixes active, ignored and
 * sync-cleared rows, and neither of the other two words covers all three.
 */
const COUNT_COLUMN_LABELS = {
    [STATUS_ACTIVE]: "Failed",
    [STATUS_IGNORED]: "Ignored"
};

export function countColumnLabel(status) {
    return COUNT_COLUMN_LABELS[status] || "Records";
}

const COUNT_QUALIFIERS = {
    [STATUS_ACTIVE]: "failed ",
    [STATUS_IGNORED]: "ignored "
};

/**
 * The toolbar's running total, qualified the same way the column is headed so
 * the two cannot describe the same number differently.
 */
export function recordCountSummary(count, status) {
    const qualifier = COUNT_QUALIFIERS[status] || "";

    return `${count} ${qualifier}record${count === 1 ? "" : "s"}`;
}
